// Cold-start gate. Extracted from Dashboard so the exact combination of
// signals that shows the first-visit view is unit-testable in isolation.
export interface FirstVisitInput {
  loading: boolean;        // Dashboard.loading — true during loadData()
  profileLoaded: boolean;  // !!customerProfile — false if the profile fetch failed
  totalPurchases: number;  // customerProfile.total_purchases — authoritative, on-chain
  firstStampDone: boolean; // !!localStorage['bitbrew_first_stamp_done']
  isShopOwner?: boolean;   // owns a shop on-chain, or the sticky flag says so
}

// Cold-start shows ONLY to a user whose profile loaded cleanly and who has
// genuinely never earned a stamp. A returning user with a failed load keeps
// profileLoaded=false and falls through to the normal dashboard.
//
// Shop owners are excluded outright. The cold-start view offers only FIND A
// SHOP and LOG OUT — the MANAGE SHOP and OWN A CAFE? actions live below this
// gate in the full dashboard. An owner with zero customer stamps was therefore
// locked out of their own shop by the UI, permanently, since the only way past
// the gate was to go and buy a coffee somewhere else.
export function isFirstVisit(input: FirstVisitInput): boolean {
  return (
    !input.loading &&
    input.profileLoaded &&
    (input.totalPurchases || 0) === 0 &&
    !input.firstStampDone &&
    !input.isShopOwner
  );
}

// Which reveal a just-confirmed stamp gets. The WELCOME beat is for the first
// stamp EVER — and "ever" is a server fact (on-chain total_purchases), not a
// device fact: gating on localStorage alone replayed "WELCOME TO THE GAME" at
// long-time customers on any cleared/new browser. The device flag still
// guards the stale-profile window (profile not yet refreshed after stamp #1
// on this device). Everything else gets the stamp-get flash.
export function isFirstStampEver(totalPurchases: number, firstStampDone: boolean): boolean {
  return (totalPurchases || 0) === 0 && !firstStampDone;
}

// Per-user localStorage key for "this user has completed their first stamp".
// Per-user (not device-global) so a new customer on a shared/previously-used
// device still sees the cold-start view and the first-stamp reveal. Falls back
// to the legacy global key when no address is present (the gate short-circuits
// on profileLoaded before this matters).
export function firstStampStorageKey(userAddress: string | null | undefined): string {
  return userAddress ? `bitbrew_first_stamp_done_${userAddress}` : 'bitbrew_first_stamp_done';
}

// Per-user "this address owns a shop" hint.
//
// fetchShopCapabilities sits in loadData's tolerated-failure group, so a
// transient RPC fault silently yields [] and would re-strand an owner in
// cold-start. This flag is sticky because owner-ness is monotonic in practice
// — you do not stop owning a café.
//
// It is a UI hint and grants no privilege: every owner route and write path
// still authorises against the on-chain capability independently, so a forged
// flag buys nothing but a dashboard whose data loads fail.
export function shopOwnerStorageKey(userAddress: string | null | undefined): string {
  return userAddress ? `bitbrew_is_shop_owner_${userAddress}` : 'bitbrew_is_shop_owner';
}
